import { apiClient } from './api'

// Tipos de documentos basados en el backend
export enum DocumentType {
  INE = 'INE',
  CURP = 'CURP',
  RFC = 'RFC',
  COMP_DOM = 'COMP_DOM',
  CONTRACT_PREPAID = 'CONTRACT_PREPAID',
  COMPANY_REGISTRATION = 'COMPANY_REGISTRATION',
  LEGAL_REPRESENTATIVE_INE = 'LEGAL_REPRESENTATIVE_INE',
  LEGAL_REPRESENTATIVE_CURP = 'LEGAL_REPRESENTATIVE_CURP',
  LEGAL_REPRESENTATIVE_RFC = 'LEGAL_REPRESENTATIVE_RFC',
  SHAREHOLDER_INE = 'SHAREHOLDER_INE',
  SHAREHOLDER_CURP = 'SHAREHOLDER_CURP',
  SHAREHOLDER_RFC = 'SHAREHOLDER_RFC',
  PROOF_OF_ADDRESS = 'PROOF_OF_ADDRESS',
  PROOF_OF_ADDRESS_OPERATIVE = 'PROOF_OF_ADDRESS_OPERATIVE',
  BANK_STATEMENT = 'BANK_STATEMENT',
  TAX_CERTIFICATE = 'TAX_CERTIFICATE',
  TAX_STATUS = 'TAX_STATUS',
}

// Interfaces para los DTOs
export interface DocumentUploadItem {
  type: DocumentType
  description?: string
  metadata?: string
}

export interface DocumentUploadResponse {
  id: string
  type: DocumentType
  originalFileName: string
  nameFile: string
  fileSize: string
  uploadedAt: string
  status: 'uploaded' | 'failed' | 'pending_verification'
  error?: string
}

export interface BatchUploadResponse {
  successful: DocumentUploadResponse[]
  failed: Array<{
    type: DocumentType
    error: string
    originalFileName?: string
  }>
  summary: {
    total: number
    successful: number
    failed: number
  }
}

export interface UserDocument {
  id: string
  nameFile: string
  originalFileName: string
  fileSize: string
  mimeType: string
  type: DocumentType
  description?: string
  verified: boolean
  active: boolean
  verificationNotes?: string
  createdAt: string
  updatedAt: string
}

// Fila de "Mis documentos" (pantalla de cuenta) — fileSize y date ya vienen
// formateados por el backend (KB/MB y moment respectivamente). url puede
// venir null cuando el documento aún no tiene un archivo descargable.
export interface DocumentGroupItem {
  id: string
  title: string
  category: string
  fileSize: string
  date: string
  status: 'Disponible'
  url: string | null
  source: 'additional' | 'userDocument'
  creditApplicationId?: string
}

export interface CreditApplicationVehicleSummary {
  brand: string
  subBrand: string
  modelYear: string | number
  version: string
  vehicleValue: number
}

// "Mis documentos" agrupados por solicitud de crédito — cada elemento trae
// además los datos de esa solicitud (vehículo, status, fecha) para poder
// distinguirlas en un selector.
export interface CreditApplicationDocumentsGroup {
  creditApplicationId: string
  status: string
  createdAt: Date
  vehicle: CreditApplicationVehicleSummary
  basicos: DocumentGroupItem[]
  estadosDeCuenta: DocumentGroupItem[]
}

export type MyCreditApplicationDocumentsByApplicationResponse =
  CreditApplicationDocumentsGroup[]

class DocumentService {
  private getUserId(): string {
    const userData = localStorage.getItem('user_data')
    if (!userData) {
      throw new Error('Usuario no autenticado')
    }
    const user = JSON.parse(userData)
    return user.id
  }

  /**
   * Sube múltiples documentos en una sola petición
   */
  async uploadDocuments(
    files: File[],
    documents: DocumentUploadItem[],
    creditApplicationId?: string | null
  ): Promise<BatchUploadResponse> {
    const userId = this.getUserId()
    const formData = new FormData()

    // Agregar archivos
    files.forEach(file => {
      formData.append('files', file)
    })

    // Agregar metadata
    formData.append('documents', JSON.stringify(documents))

    if (creditApplicationId) {
      formData.append('creditApplicationId', creditApplicationId)
    }

    console.log('Uploading documents:', {
      filesCount: files.length,
      documents,
      userId,
      creditApplicationId,
    })

    const response = await apiClient.post(
      `/user/${userId}/documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 segundos para uploads múltiples
      }
    )

    console.log('Upload response:', response.data)

    // La respuesta viene como { success: true, data: {...} }
    // Necesitamos extraer el data
    if (response.data && response.data.success && response.data.data) {
      console.log('Returning nested data:', response.data.data)
      return response.data.data
    }

    // Fallback si la estructura es diferente
    console.log('Returning raw response data:', response.data)
    return response.data
  }

  /**
   * Sube un solo documento
   */
  async uploadSingleDocument(
    file: File,
    document: DocumentUploadItem,
    creditApplicationId?: string | null
  ): Promise<DocumentUploadResponse> {
    const userId = this.getUserId()
    const formData = new FormData()

    formData.append('file', file)
    formData.append('type', document.type)

    if (document.description) {
      formData.append('description', document.description)
    }

    if (document.metadata) {
      formData.append('metadata', document.metadata)
    }

    if (creditApplicationId) {
      formData.append('creditApplicationId', creditApplicationId)
    }

    const response = await apiClient.post(
      `/user/${userId}/documents/upload-single`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 segundos para upload individual
      }
    )

    return response.data
  }

  /**
   * Obtiene todos los documentos del usuario
   */
  async getUserDocuments(
    type?: DocumentType,
    verified?: boolean
  ): Promise<UserDocument[]> {
    const userId = this.getUserId()
    const params = new URLSearchParams()

    if (type) {
      params.append('type', type)
    }

    if (verified !== undefined) {
      params.append('verified', verified.toString())
    }

    const response = await apiClient.get(
      `/user/${userId}/documents?${params.toString()}`
    )

    return response.data
  }

  /**
   * Elimina un documento
   */
  async deleteDocument(documentId: string): Promise<{ message: string }> {
    const userId = this.getUserId()

    const response = await apiClient.delete(
      `/user/${userId}/documents/${documentId}`
    )

    return response.data
  }

  /**
   * Verifica un documento (solo para admins)
   */
  async verifyDocument(
    documentId: string,
    verified: boolean,
    notes?: string
  ): Promise<UserDocument> {
    const userId = this.getUserId()

    const response = await apiClient.patch(
      `/user/${userId}/documents/${documentId}/verify`,
      { verified, notes }
    )

    return response.data
  }

  /**
   * Utilidad para validar archivos antes del upload
   */
  validateFile(
    file: File,
    type: DocumentType
  ): { valid: boolean; error?: string } {
    // Validar tamaño (30MB máximo)
    const maxSize = 30 * 1024 * 1024 // 30MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'El archivo excede el tamaño máximo de 30MB',
      }
    }

    // Validar tipo MIME
    const allowedMimeTypes = ['image/jpeg', 'image/png', 'application/pdf']
    if (!allowedMimeTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de archivo no permitido. Solo se aceptan JPEG, PNG y PDF',
      }
    }

    // Validaciones específicas por tipo de documento
    switch (type) {
      case DocumentType.BANK_STATEMENT:
        if (file.type !== 'application/pdf') {
          return {
            valid: false,
            error: 'Los estados de cuenta deben ser archivos PDF',
          }
        }
        break

      case DocumentType.INE:
      case DocumentType.LEGAL_REPRESENTATIVE_INE:
      case DocumentType.SHAREHOLDER_INE:
        if (
          !['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)
        ) {
          return {
            valid: false,
            error: 'Los documentos INE deben ser archivos JPEG, PNG o PDF',
          }
        }
        break
    }

    return { valid: true }
  }

  /**
   * Documentos de "Mis documentos" (cuenta del usuario): una entrada por
   * cada solicitud de crédito donde es aplicante o aval, cada una con sus
   * documentos agrupados en básicos / estados de cuenta. No requiere
   * userId en el path — el backend resuelve todo por el JWT. La doc dice
   * que la respuesta es el arreglo directo, sin envoltura, pero por si un
   * interceptor global del backend la envuelve igual (p. ej. { data: [...] }
   * como el resto de la API), se valida la forma real antes de regresarla.
   */
  async getMyCreditApplicationDocumentsByApplication(): Promise<MyCreditApplicationDocumentsByApplicationResponse> {
    const response = await apiClient.get(
      '/credit-application/my-documents/by-application'
    )

    if (Array.isArray(response.data)) {
      return response.data
    }

    if (Array.isArray(response.data?.data)) {
      return response.data.data
    }

    return []
  }

  /**
   * Utilidad para obtener los límites de documentos por tipo
   */
  getDocumentLimits(): Record<DocumentType, number> {
    return {
      [DocumentType.COMPANY_REGISTRATION]: 3,
      [DocumentType.BANK_STATEMENT]: 6,
      [DocumentType.PROOF_OF_ADDRESS]: 1,
      [DocumentType.PROOF_OF_ADDRESS_OPERATIVE]: 1,
      [DocumentType.INE]: 1,
      [DocumentType.CURP]: 1,
      [DocumentType.RFC]: 1,
      [DocumentType.LEGAL_REPRESENTATIVE_INE]: 1,
      [DocumentType.LEGAL_REPRESENTATIVE_CURP]: 1,
      [DocumentType.LEGAL_REPRESENTATIVE_RFC]: 1,
      [DocumentType.SHAREHOLDER_INE]: 1,
      [DocumentType.SHAREHOLDER_CURP]: 1,
      [DocumentType.SHAREHOLDER_RFC]: 1,
      [DocumentType.TAX_CERTIFICATE]: 1,
      [DocumentType.TAX_STATUS]: 1,
      [DocumentType.COMP_DOM]: 1,
      [DocumentType.CONTRACT_PREPAID]: 5,
    }
  }
}

export const documentService = new DocumentService()
export default documentService
